import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useProbateTheme, useSelectedCaseRef } from "./utils/hooks";
import Action from "../../components/Action";
import Card from "../../components/DashboardCard";
import DynamicFormDoable from "./DynamicFormDoable";
import DocumentUploadDoable from "./DocumentUploadDoable";
import DynamicFormSubmission from "./DynamicFormSubmission";
import DocumentUploadSubmission from "./DocumentUploadSubmission";
import EnquiryDoable from "./EnquiryDoable";
import EmailedDoable from "./EmailedDoable";
import ProbateDoable from "./probate/ProbateDoable";
import ProbateEnquiry from "./probate/ProbateEnquiry";
import { normaliseInformationRequestConfiguration } from "../../shared/informationRequest/informationRequestConfiguration";
import { reportDoableLoadFailure } from "../../utils/sentryUtils";

const CaseDoable = () => {
  const sessionType = useSelector((state) => state.dashboardState.session.session_type);

  const caseRef = useSelectedCaseRef();
  const probateCase = useProbateTheme();
  const { doableId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [errored, setErrored] = useState(false);
  const [doable, setDoable] = useState(null);

  useEffect(() => {
    setLoading(true);
    setErrored(false);
    const controller = new AbortController();

    const loadDoable = async () => {
      try {
        const resp = await fetch("/api/dashboard/doable/" + doableId, {
          method: "GET",
          credentials: "same-origin",
          signal: controller.signal,
        });

        if (!resp.ok) {
          reportDoableLoadFailure(resp, { action: "LoadDoable", doableId });
          setErrored(true);
          setLoading(false);
          return;
        }

        const data = await resp.json();

        // Normalise information requests at this boundary. Other doable types keep
        // their own configuration shape; adapter errors follow the fetch-failure path.
        setDoable(
          data.doable_type === "information_request"
            ? { ...data, configuration: normaliseInformationRequestConfiguration(data.configuration) }
            : data,
        );
        setLoading(false);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Failed to load doable details", err);
        reportDoableLoadFailure(err, { action: "LoadDoable", doableId });
        setErrored(true);
        setLoading(false);
      }
    };

    loadDoable();
    return () => controller.abort();
  }, [doableId]);

  // TODO: extract this into something shared with DashboardNav
  const returnToDashboard = () => {
    if (!!caseRef) {
      navigate(`/dashboard/${caseRef}`);
    } else {
      navigate("/dashboard");
    }
  };

  if (loading) {
    return null;
  }

  if (errored) {
    return (
      <React.Fragment>
        <Card title="Sorry, that didn’t work" removeTopPadding headingSize="2">
          <p>Failed to load request details.</p>
          <Action small onClick={returnToDashboard}>
            Take me back to try again
          </Action>
        </Card>
      </React.Fragment>
    );
  }

  let DoableComponent;
  switch (doable.doable_type) {
    case "information_request":
      if (probateCase) {
        DoableComponent = ProbateDoable;
      } else if (doable.status === "complete") {
        DoableComponent = DynamicFormSubmission;
      } else {
        DoableComponent = DynamicFormDoable;
      }
      break;
    case "document_request":
      if (doable.status === "complete") {
        DoableComponent = DocumentUploadSubmission;
      } else {
        DoableComponent = DocumentUploadDoable;
      }
      break;
    case "enquiry":
      DoableComponent = probateCase ? ProbateEnquiry : EnquiryDoable;
      break;
    default:
      // "emailed_request" or any other unexpected doable type
      DoableComponent = EmailedDoable;
  }

  const submissionEnabled = sessionType === "client";

  return (
    <DoableComponent
      key={doable.id}
      doable={doable}
      caseRef={caseRef}
      submissionEnabled={submissionEnabled}
      returnToDashboard={returnToDashboard}
    />
  );
};

export default CaseDoable;
